(() => {
  const KEY = '__capRequestCircuitV1';
  if (window[KEY]) return;

  const TRANSIENT_STATUS = new Set([
    408, 425, 429,
    500, 502, 503, 504, 507, 508, 509, 520, 521, 522, 523, 524
  ]);
  const FAILURE_WINDOW_MS = 15000;
  const INITIAL_COOLDOWN_MS = 8000;
  const MAX_COOLDOWN_MS = 60000;
  const state = { consecutiveFailures: 0, lastFailureAt: 0, openedUntil: 0, cooldownMs: INITIAL_COOLDOWN_MS };
  const now = () => Date.now();

  function retryAfterMs(response) {
    const raw = response?.headers?.get?.('Retry-After');
    if (!raw) return 0;
    const seconds = Number(raw);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
    const timestamp = Date.parse(raw);
    return Number.isFinite(timestamp) ? Math.max(0, timestamp - now()) : 0;
  }

  function isTransient(value) {
    if (!value) return true;
    if (value.name === 'AbortError' || value.name === 'CapCircuitOpen') return false;
    const status = Number(value.status || value.response?.status || 0);
    if (status) return TRANSIENT_STATUS.has(status);
    if (value instanceof Response) return !value.ok;
    return true;
  }

  function resetAfterSuccess() {
    state.consecutiveFailures = 0;
    state.lastFailureAt = 0;
    state.openedUntil = 0;
    state.cooldownMs = INITIAL_COOLDOWN_MS;
  }

  function recordFailure(value) {
    if (!isTransient(value)) return;
    const timestamp = now();
    if (timestamp - state.lastFailureAt > FAILURE_WINDOW_MS) state.consecutiveFailures = 0;
    state.lastFailureAt = timestamp;
    state.consecutiveFailures += 1;
    if (state.consecutiveFailures < 3) return;
    const cooldown = Math.max(state.cooldownMs, retryAfterMs(value));
    state.openedUntil = timestamp + cooldown;
    state.cooldownMs = Math.min(MAX_COOLDOWN_MS, Math.max(INITIAL_COOLDOWN_MS, cooldown * 2));
  }

  function beforeRequest(signal) {
    if (signal?.aborted) throw new DOMException('The request was aborted.', 'AbortError');
    const remaining = state.openedUntil - now();
    if (remaining <= 0) return;
    const error = new Error('Search requests are temporarily paused after repeated server failures.');
    error.name = 'CapCircuitOpen';
    error.retryAfterMs = remaining;
    throw error;
  }

  async function run(task, signal) {
    beforeRequest(signal);
    try {
      const response = await task();
      if (response && response.ok === false) recordFailure(response);
      else resetAfterSuccess();
      return response;
    } catch (error) {
      if (error?.name !== 'AbortError' && error?.name !== 'CapCircuitOpen') recordFailure(error);
      throw error;
    }
  }

  window[KEY] = Object.freeze({
    run,
    isOpen: () => state.openedUntil > now(),
    snapshot: () => ({
      consecutiveFailures: state.consecutiveFailures,
      openedUntil: state.openedUntil,
      cooldownMs: state.cooldownMs
    })
  });
})();